import { Component, computed, inject, Input, input, output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import {
  EditSiteBudgetNameModalComponent,
  EditSiteBudgetNameModalData,
} from '@features/edit-site-budget-name-modal/edit-site-budget-name-modal.component';
import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';

import {
  injectDeleteSiteBudgetVersionMutation,
  injectSiteBudget,
} from '@models/sites/api/site-budget.api';

import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import {
  injectNgControl,
  NoopValueAccessorDirective,
} from '@shared/directives/noop-value-accessor.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { listSiteBudgetVersionsQuery } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { Maybe, Utils } from '@shared/utils/utils';

@Component({
  hostDirectives: [NoopValueAccessorDirective, SiteWorkflowDirective],
  imports: [
    NgSelectModule,
    ReactiveFormsModule,
    IconComponent,
    TooltipDirective,
    ErrorsTooltipComponent,
  ],
  selector: 'aux-site-budget-dropdown',
  styles: `
    ::ng-deep #siteBudgetVersion .ng-option-selected::after {
      margin-top: 12px !important;
    }
  `,
  template: `
    <div class="min-w-52">
      <p class="mb-1 text-xs">{{ label() }}</p>
      <ng-select
        [formControl]="ngControl.control"
        id="siteBudgetVersion"
        [items]="siteBudgetOptions()"
        [placeholder]="placeholder"
        [loading]="siteBudget.isLoading()"
        [searchable]="true"
        [clearable]="false"
        bindLabel="name"
        bindValue="id"
        [auxTooltip]="tooltip()"
      >
        <ng-template ng-option-tmp let-item="item">
          @let selected = item.id === ngControl.control.value;

          <div class="flex items-center justify-between">
            <p
              class="truncate"
              #siteBudgetName
              [auxTooltip]="
                siteBudgetName.offsetWidth < siteBudgetName.scrollWidth ? item.name : ''
              "
            >
              {{ item.name }}
            </p>

            @if (!hideOptionActions()) {
              @let reasons = siteWorkflow.updateSiteCostTooltips();

              <div class="flex items-center space-x-2" [class.pr-2]="selected">
                @if (!item.is_baseline) {
                  <aux-icon
                    name="Trash"
                    class="text-aux-red-dark"
                    [size]="16"
                    [class.cursor-not-allowed]="siteWorkflow.disabledUpdateSiteCostUI()"
                    [class.opacity-50]="siteWorkflow.disabledUpdateSiteCostUI()"
                    [auxTooltip]="reasons.length === 1 ? reasons[0] : ''"
                    [template]="reasons.length > 1 ? updateSiteCostTooltipsTemplate : undefined"
                    (click)="deleteSiteBudgetVersion(item)"
                  />
                }
                <aux-icon
                  name="Pencil"
                  class="text-aux-blue"
                  [size]="16"
                  [class.cursor-not-allowed]="siteWorkflow.disabledUpdateSiteCostUI()"
                  [class.opacity-50]="siteWorkflow.disabledUpdateSiteCostUI()"
                  [auxTooltip]="reasons.length === 1 ? reasons[0] : ''"
                  [template]="reasons.length > 1 ? updateSiteCostTooltipsTemplate : undefined"
                  (click)="editSiteBudgetName(item)"
                />
              </div>

              <ng-template #updateSiteCostTooltipsTemplate>
                <aux-errors-tooltip [errors]="reasons" />
              </ng-template>
            }
          </div>
        </ng-template>
      </ng-select>
    </div>
  `,
})
export class SiteBudgetDropdownComponent {
  overlayService = inject(OverlayService);
  siteWorkflow = inject(SiteWorkflowDirective);

  deleteSiteBudgetVersionMutation = injectDeleteSiteBudgetVersionMutation();
  ngControl = injectNgControl();

  selectedSiteId = input.required<Maybe<string>>();

  siteBudget = injectSiteBudget({ site_id: this.selectedSiteId });

  selectedProtocolVersion = input.required<Maybe<string>>();

  optionsChanged = output<listSiteBudgetVersionsQuery[]>();

  siteBudgetOptions = computed(() => {
    const siteBudget = this.siteBudget.data() || [];
    const protocolVersion = this.selectedProtocolVersion();

    const options = siteBudget
      .filter(({ patient_protocol_version_id }) => patient_protocol_version_id === protocolVersion)
      .sort(({ create_date }, { create_date: create_date2 }) =>
        Utils.dateSort(create_date, create_date2)
      );

    this.optionsChanged.emit(options);

    return options;
  });

  hideOptionActions = input(false);
  label = input('Site Budget Version');
  tooltip = input('');

  @Input() placeholder = '';

  async deleteSiteBudgetVersion(budgetVersion: listSiteBudgetVersionsQuery) {
    if (this.siteWorkflow.disabledUpdateSiteCostUI()) {
      return;
    }

    const modalEvent = this.overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        keywordToExecuteAction: 'Delete Site Budget',
        message:
          'This will permanently delete the selected site budget amendment under this Protocol Version. This action cannot be undone and will remove all associated data including all visit and invoiceable costs.',
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Delete Site Budget Amendment?',
        primaryButton: {
          label: 'Delete Site Budget',
          variant: 'negative',
        },
      },
    }).afterClosed$;

    const resp = await firstValueFrom(modalEvent);

    if (resp.data) {
      await this.deleteSiteBudgetVersionMutation.mutateAsync({
        id: budgetVersion.id,
        site_id: budgetVersion.site_id,
      });
    }
  }

  editSiteBudgetName(budgetVersion: listSiteBudgetVersionsQuery) {
    if (this.siteWorkflow.disabledUpdateSiteCostUI()) {
      return;
    }

    this.overlayService.openPopup({
      content: EditSiteBudgetNameModalComponent,
      data: <EditSiteBudgetNameModalData>{
        budgetVersion,
      },
      settings: {
        header: 'Edit Name',
        primaryButton: {
          action: async (instance: EditSiteBudgetNameModalComponent | null) => {
            await instance?.updateSiteAmendmentName();
          },
          disabled: (instance: EditSiteBudgetNameModalComponent | null) => {
            return !!instance?.siteBudgetName.invalid;
          },
        },
      },
    });
  }
}
